/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { CarouselTemplate } from "../../model/enums";

/**
 * Dimensions for a single tile template expressed in viewport-relative units.
 * All values must stay in sync with Tile.scss variables.
 */
type TTileDimensions = {
  /** Tile width as a numeric vw value. */
  widthVw: number;
  /** Image-container aspect ratio as a decimal fraction (height / width). */
  aspectRatio: number;
  /**
   * Height of the tile's info block (`.tile__info`) as a numeric vw value.
   *
   * Carousels never need this — a rail is one row, so the row's height is
   * whatever the tiles turn out to be. A grid does: `ContentGrid` lays cells
   * out on a fixed row pitch, so it has to be told up front how tall a whole
   * tile is, info block included. Kept here rather than in the grid so tile
   * geometry stays in one place.
   */
  infoHeightVw: number;
};

/** Pixel dimensions for image URL generation and other fixed-size consumers. */
type TTilePixelDimensions = {
  width: number;
  height: number;
};

/** Base viewport width used to convert vw values to px dimensions. */
const DEFAULT_BASE_VIEWPORT_WIDTH_PX: number = 1920;

// ---------------------------------------------------------------------------
// Per-template dimension constants
// ---------------------------------------------------------------------------

/** Standard 16 ∶ 9 landscape tile — "On Now / Upcoming" carousels. */
const LANDSCAPE_HORIZONTAL_DIMENSIONS: TTileDimensions = {
  widthVw: 21.927,
  aspectRatio: 0.5625,
  infoHeightVw: 8.958
};

/** Same viewport width as landscape, used for grid-accessible carousels. */
const LANDSCAPE_HORIZONTAL_GRID_DIMENSIONS: TTileDimensions = {
  widthVw: 21.927,
  aspectRatio: 0.5625,
  infoHeightVw: 8.958
};

/**
 * PIP landscape horizontal tile — 16 ∶ 9 tile matching landscape_horizontal width.
 * Maps to the PIP_LANDSCAPE_HORIZONTAL carousel template.
 */
const PIP_LANDSCAPE_HORIZONTAL_DIMENSIONS: TTileDimensions = {
  widthVw: 21.927,
  aspectRatio: 0.5625,
  infoHeightVw: 8.958
};

/**
 * Recently Watched tile — 16 ∶ 9 tile at the smaller hero-slot width.
 * Maps to the LOGO_PIP_LANDSCAPE_HORIZONTAL carousel template.
 */
const LOGO_PIP_LANDSCAPE_HORIZONTAL_DIMENSIONS: TTileDimensions = {
  widthVw: 18.75,
  aspectRatio: 0.5625,
  infoHeightVw: 4.375
};

/** Grid-accessible variant of the recently-watched (PIP) tile. */
const PIP_LANDSCAPE_GRID_DIMENSIONS: TTileDimensions = {
  widthVw: 18.75,
  aspectRatio: 0.5625,
  infoHeightVw: 4.375
};

/**
 * Poster tile (2 ∶ 3) — "Top 10 for You" carousels.
 * Width  : 250.66665649414062 px → 13.056vw at 1920 px base.
 * Height : 376 px → 34.815vh at 1080 px base.
 * Aspect : 376 / 250.67 ≈ 1.5 (2 ∶ 3 ratio).
 */
const POSTER_HORIZONTAL_DIMENSIONS: TTileDimensions = {
  widthVw: 13.056,
  aspectRatio: 1.5,
  infoHeightVw: 5.677
};

/**
 * Square logo tile (1 ∶ 1) — channels / leagues / teams.
 * 251 × 251 px at the 1920 base (Figma v5.0 "Channels" thumbnail) → 13.056vw,
 * the same column width as the poster tile so logo and poster rails align.
 * Must stay in sync with $tile-logo-width in Tile.scss.
 */
const LOGO_HORIZONTAL_DIMENSIONS: TTileDimensions = {
  widthVw: 13.056,
  aspectRatio: 1,
  infoHeightVw: 5.677
};

// ---------------------------------------------------------------------------
// Lookup map — keyed by CarouselTemplate enum value
// ---------------------------------------------------------------------------

const TILE_DIMENSION_MAP: Record<CarouselTemplate, TTileDimensions> = {
  [CarouselTemplate.LANDSCAPE_HORIZONTAL]: LANDSCAPE_HORIZONTAL_DIMENSIONS,
  [CarouselTemplate.LANDSCAPE_GRID]: LANDSCAPE_HORIZONTAL_GRID_DIMENSIONS,
  [CarouselTemplate.PIP_LANDSCAPE_HORIZONTAL]:
    PIP_LANDSCAPE_HORIZONTAL_DIMENSIONS,
  [CarouselTemplate.LOGO_PIP_LANDSCAPE_HORIZONTAL]:
    LOGO_PIP_LANDSCAPE_HORIZONTAL_DIMENSIONS,
  [CarouselTemplate.PIP_LANDSCAPE_GRID]: PIP_LANDSCAPE_GRID_DIMENSIONS,
  [CarouselTemplate.POSTER_HORIZONTAL]: POSTER_HORIZONTAL_DIMENSIONS,
  [CarouselTemplate.LOGO_HORIZONTAL]: LOGO_HORIZONTAL_DIMENSIONS
};

/** Fallback used when the template is not found in the map. */
const DEFAULT_TILE_DIMENSIONS: TTileDimensions =
  LANDSCAPE_HORIZONTAL_DIMENSIONS;

/**
 * Maps each `CarouselTemplate` to the CSS class applied to the root tile element.
 * The class drives all per-template sizing rules in Tile.scss.
 */
const CarouselTemplateMapper: Record<CarouselTemplate, string> = {
  [CarouselTemplate.LANDSCAPE_HORIZONTAL]: "tile--landscape_horizontal",
  [CarouselTemplate.LANDSCAPE_GRID]: "tile--landscape_grid",
  [CarouselTemplate.PIP_LANDSCAPE_HORIZONTAL]: "tile--pip_landscape_horizontal",
  [CarouselTemplate.LOGO_PIP_LANDSCAPE_HORIZONTAL]:
    "tile--logo_pip_landscape_horizontal",
  [CarouselTemplate.PIP_LANDSCAPE_GRID]: "tile--pip_landscape_grid",
  [CarouselTemplate.POSTER_HORIZONTAL]: "tile--poster_horizontal",
  [CarouselTemplate.LOGO_HORIZONTAL]: "tile--logo_horizontal"
};

/**
 * Returns the viewport-relative dimensions (width, aspect ratio) for the given
 * carousel template.
 *
 * @param template - The `CarouselTemplate` value for which to look up dimensions.
 * @returns `TTileDimensions` for the requested template, or the landscape
 *          defaults when the template is not found.
 */
const getTileDimensions = (template: CarouselTemplate): TTileDimensions => {
  const dimensions: TTileDimensions | undefined = TILE_DIMENSION_MAP[template];
  if (dimensions === undefined) {
    return DEFAULT_TILE_DIMENSIONS;
  }
  return dimensions;
};

/**
 * Returns pixel dimensions (width and height) for the given carousel template.
 *
 * @param template - The `CarouselTemplate` value for which to compute dimensions.
 * @param baseViewportWidthPx - Base viewport width used for vw-to-px conversion.
 * @returns Pixel width/height rounded to the nearest integer.
 */
const getTilePixelDimensions = (
  template: CarouselTemplate,
  baseViewportWidthPx: number = DEFAULT_BASE_VIEWPORT_WIDTH_PX
): TTilePixelDimensions => {
  const tileDimensions: TTileDimensions = getTileDimensions(template);
  const width: number = Math.round(
    (tileDimensions.widthVw / 100) * baseViewportWidthPx
  );
  const height: number = Math.round(width * tileDimensions.aspectRatio);

  return {
    width,
    height
  };
};

/**
 * Total height of a whole tile — image container plus info block — in vw.
 *
 * The image container is sized by `padding-bottom: <aspect>%` of the tile
 * width, so its height is `width * aspectRatio`; the info block sits below it
 * at a fixed per-template height. Grids need the sum in order to set a row
 * pitch; carousels do not, because a rail is a single row.
 *
 * @param template - The `CarouselTemplate` whose tile is being measured.
 * @returns Height as a numeric vw value.
 */
const getTileTotalHeightVw = (template: CarouselTemplate): number => {
  const dimensions: TTileDimensions = getTileDimensions(template);
  return dimensions.widthVw * dimensions.aspectRatio + dimensions.infoHeightVw;
};

export type { TTileDimensions, TTilePixelDimensions };

/**
 * The template enum itself, re-exported.
 *
 * Every consumer of the helpers below is already holding a `CarouselTemplate`,
 * and views must not reach into `src/model/**` for it (MVVM: model → viewModel
 * → views, enforced by scripts/lint-mvvm.cjs). Surfacing it from the module
 * that owns the per-template arithmetic keeps it one import rather than two.
 */
export { CarouselTemplate };
export {
  getTileDimensions,
  getTilePixelDimensions,
  getTileTotalHeightVw,
  TILE_DIMENSION_MAP,
  DEFAULT_TILE_DIMENSIONS,
  CarouselTemplateMapper
};
